import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { EChartsOption } from "echarts";

interface sensorItem {
  id: string;
  showDialog: boolean;
  chartWidth: number;
  chartHeight: number;
  setLastUpdate: (timestamp: string) => void; 
}

export interface ResultItem {
  id: string;
  value: number;
  timestamp: string;
}

const SideRainCurrentChart: React.FC<sensorItem> = ({
  id,
  showDialog,
  chartWidth,
  chartHeight,
  setLastUpdate
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartInstance, setChartInstance] = useState<echarts.ECharts>();
  const [initialData, setInitialData] = useState<any[]>([]);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const RAIN_CURRENT_THREE_URL = process.env.REACT_APP_RAIN_CURRENT_THREE_URL;
  
  useEffect(() => {
    const fetchDataAndInitializeChart = async () => {
      try {
        const response = await fetch(
          `${RAIN_CURRENT_THREE_URL}${id}`
        );
        const responseData = await response.json();
  
        if (responseData && Array.isArray(responseData.results)) {
          // Process the results array
          const data = responseData.results
            .map((item: ResultItem) => {
              const cleanTimestamp = item.timestamp.replace(/\+\d{2}\.\d{3}Z$/, "+01:00");
              const parsedDate = new Date(cleanTimestamp);
  
              if (isNaN(parsedDate.getTime())) {
                console.error("Invalid timestamp:", item.timestamp);
                return null;
              }
              return [parsedDate.toISOString(), item.value];
            })
            .filter((entry:any): entry is [string, number] => entry !== null);
          if (data.length > 0) {
            setInitialData(data);
          } else {
            console.error("No valid data points found:", responseData.results);
          }
        } else {
          console.error("Unexpected response data format:", responseData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
  
    fetchDataAndInitializeChart();
  }, [id]);
  
      

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  useEffect(() => {

    if(chartWidth !== 0 || chartHeight !== 0){
      setInitialLoad(false);
    }
  }, [chartWidth,initialLoad,chartHeight]);

  useEffect(() => {
    if (
      chartRef.current &&
      chartRef.current.clientWidth &&
      chartRef.current.clientHeight
    ) {
      const chart = echarts.init(chartRef.current);

      const chartOption: EChartsOption = {
    
        color: ["#80FFA5", "#00DDFF", "#37A2FF", "#FF0087", "#FFBF00"],

        title: {
          left: "center",
          text: "Current Rainfall",
          textStyle: {
            fontSize:"0.8rem",
            color: "#03FE03",
          },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
            label: {
              backgroundColor: "#6a7985",
            },
          },
        },
        // legend: {
        //   data: [name],
        //   textStyle: {
        //     color: "#FFFFFF",
        //   },
        //   right: "10%",
        //   top: "10%",
        // },
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: "none",
            },
            restore: {},
            saveAsImage: {},
          },
        },

        grid: {
          left: "3%",
          right: "14%",
          bottom: "20%",
          containLabel: true,
        },
        xAxis: {
          type: "time",
          name: "Time",
          axisLabel: {
            color: "#FFFFFF",
      
          },
          //   boundaryGap: [0, "100%"],
          axisLine: {
            lineStyle: {
              color: "#FFFFFF",
              width: 2,
            },
          },
        },
        yAxis: {
          type: "value",
          name: "mm",
          nameTextStyle: {
            color: "#FFFFFF",
          },
          axisLabel: {
            formatter: "{value} mm",
            color: "#FFFFFF",
          },
          boundaryGap: [0, "100%"],
          axisLine: {
            lineStyle: {
              color: "#070807",
              width: 2,
            },
          },
          splitLine: {
            lineStyle: {
              color: "rgba(85, 84, 95, 0.6)",
              width: 1,
            },
          },
          // ...splitLineStyle,
        },

        dataZoom: [
          {
            type: "inside",
            start: 0,
            end: 100,
          },
          {
            start: 0,
            end: 100,
          },
        ],
        visualMap: {
          type: "piecewise",
          show: false,
          dimension: 1,
          pieces: [
            {
              gt: 0,
              lt: 1000,
              color: "#1e8ce6",
            },
            
          ],
        },
        series: [
          {
            name:"Current Rainfall",
            type: "line",
            stack: "Total",
            smooth: true,
            symbol: "none",
            lineStyle: {
              width: 1,
            },
            areaStyle: {
              opacity: 0.8,
            },
            emphasis: {
              focus: "series",
            },
            data: initialData,
       
          },
        ],
      };

      chart.setOption(chartOption);
      setChartInstance(chart);
    }

    return () => {
      if (chartInstance) {
        chartInstance.dispose();
      }
    };
  }, [initialData]);

  useEffect(() => {
    if (chartInstance) {
      chartInstance.resize();
    }
  }, [chartWidth,chartHeight]);

  useEffect(() => {
    const handleResize = () => {
      if (chartInstance) {
        chartInstance.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [chartInstance]);

  return (
    <div
      id="main"
      style={{
        width: initialLoad ? "100%" : `${chartWidth}px`,
        height: initialLoad?"45vh" :`${chartHeight}px`}} 
      ref={chartRef}
    ></div>
  );
};

export default SideRainCurrentChart;

